import React, { useMemo, useState } from "react";
import { match, Route } from "react-router-dom";
import useAppContext from "../../../context/useAppContext";
import useLogs from "../../utils/useLogs";
import SearchBar from "../searchbar/SearchBar";
import EmptyState from "../table/EmptyState";
import LogEntryRow from "../table/LogEntryRow";
import LogSearchUpgradeBanner from "../shared/LogSearchUpgradeBanner";
import MergeTable from "../../../shared/MergeTable";
import TableSkeletonLoader from "../../shared/TableSkeletonLoader";
import { isBillingPlanLaunchPlan } from "../../settings/billing/BillingUtils";
import {
  fetchLogEntryByID,
  fetchLogEntryResponseBody,
} from "../../utils/IntegrationsManagementAPIClient";
import { LinkedAccount } from "../../../../models/Entities";
import { LogSidePanel } from "../side-panel/shared-components/LogSidePanel";

interface Props {
  match: match;
  linkedAccount?: LinkedAccount | null;
}

/**
 * The whole of the Logs page is contained within this element. Allows searching,
 * paging, etc of all current logs. Title of the page comes in from elsewhere.
 */
const LogsPage = ({ match, linkedAccount }: Props) => {
  const { paginationComponent, logs, isLoading, allFilters, applyFilters, setCurrentPageToken } =
    useLogs({ linkedAccount });

  const { user } = useAppContext();

  // Each search pill needs an associated focus value - only the last interacted should keep focus in most cases - keyed by id
  const [searchPillFocusId, setSearchPillFocusId] = useState<string | null>(null);

  const header = (
    <>
      <th scope="col">Method</th>
      <th scope="col">URL</th>
      <th scope="col">Direction</th>
      <th scope="col">Status</th>
      {!linkedAccount && <th scope="col">Integration</th>}
      {!linkedAccount && <th scope="col">Organization</th>}
      <th scope="col">Date</th>
      <th style={{ width: "24px" }} />
    </>
  );

  const content = useMemo(() => {
    if (isLoading) {
      return <TableSkeletonLoader cols={linkedAccount ? 6 : 8} rows={10} />;
    } else if (!logs || logs.length <= 0) {
      return <EmptyState />;
    } else {
      // Make sure we close any focus when we push, otherwise coming back opens a dropdown
      return logs.map((log) => (
        <LogEntryRow
          key={log.id}
          log={log}
          match={match}
          linkedAccount={linkedAccount}
          onPush={() => setSearchPillFocusId(null)}
        />
      ));
    }
  }, [isLoading, linkedAccount, logs, match]);

  return (
    <>
      {isBillingPlanLaunchPlan(user.organization.organization_billing_plan?.billing_plan) && (
        <LogSearchUpgradeBanner />
      )}
      <div className={linkedAccount ? "mb-6" : "mb-9"}>
        <SearchBar
          linkedAccount={linkedAccount}
          allFilters={allFilters}
          executeSearch={applyFilters}
          // TODO: @dgattey when saved/recent searches are around, this function should filter them when user types (even if they haven't pressed enter)
          filterSearchesBySearchTerm={() => {}}
          searchPillFocusId={searchPillFocusId}
          setSearchPillFocusId={setSearchPillFocusId}
          setCurrentPageToken={setCurrentPageToken}
        />
      </div>
      <MergeTable
        header={header}
        content={content}
        hasMorePaddingOnFirstElement
        shouldMaskFrigade
      />
      {paginationComponent}
      <Route exact path={`${match.url}/:logID`}>
        {({ match: logMatch }) => (
          <LogSidePanel
            title="API request log"
            logID={logMatch?.params.logID}
            fetchLog={fetchLogEntryByID}
            fetchResponseBody={fetchLogEntryResponseBody}
          />
        )}
      </Route>
    </>
  );
};

export default LogsPage;
